import { useState, type MouseEvent } from 'react';
import APILink, { downloadPassFile } from "@/components/shared/api-link";
import { Download } from 'lucide-react';
import Swal from "sweetalert2";
import useBookingUrl from "@/hooks/use-booking-url";
import priseRDV from "@/assets/images/resiliation/prise-rdv.png";

// Motifs pour lesquels on propose une prise de rendez-vous avant le téléchargement du RI
const RDV_REASONS = [
    'Je change de moto',
    'Je souhaite résilier mon contrat',
    'Je souhaite faire un devis à la concurrence',
    'Autre motif',
];

const linkClassName = "inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover transition-all duration-300 ease-in-out cursor-pointer no-underline";

export interface FormReleveInformationProps {
    riLink: string;
    title?: string;
    contractId?: string | number;
    hasContractApporteur43397?: boolean;
}

const FormReleveInformation = (props: FormReleveInformationProps) => {
    const { riLink, title, contractId, hasContractApporteur43397 } = props
    const [raison, setRaison] = useState<string | null>(null)
    const bookingUrl = useBookingUrl('fidelisation', (contractId ? { contract_id: contractId } : {}) as Record<string, string>, hasContractApporteur43397)

    const downloadHref = `${riLink}?reason=${raison}`

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        if (!raison) {
            return e.preventDefault()
        }
        setRaison(null)
    }

    const handleDownloadWithRDV = () => {
        void Swal.fire({
            html: `
                <div class="popup-fidelisation text-center">
                    <p class="font-bold" style="color: #104373;">Vous envisagez de nous quitter ?</p>
                    <p class="font-bold mt-4 mb-0" style="color: #104373;">Prenez un instant avant de vous décider...</p>
                    <p class="font-bold" style="color: #104373;">Laissez-nous vous proposer une solution personnalisée afin de continuer la route ensemble.</p>
                    <button type="button" id="btn-rdv-ri" class="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover mt-4">Je prends rendez-vous et télécharge mon relevé d'informations</button>
                    <img src="${priseRDV}" alt="Prise de rendez-vous" class="popup-fidelisation-img my-6 block mx-auto" style="max-width: 250px;" />
                    <button type="button" id="btn-dl-ri" class="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none btn-outline-secondary">Je télécharge seulement mon relevé d'informations</button>
                </div>
            `,
            showConfirmButton: false,
            showCloseButton: true,
            didOpen: () => {
                const btnRdv = document.getElementById('btn-rdv-ri');
                const btnDl = document.getElementById('btn-dl-ri');
                if (btnRdv) {
                    btnRdv.addEventListener('click', () => {
                        window.open(bookingUrl as string, '_blank', 'noopener,noreferrer');
                        void downloadPassFile(downloadHref);
                    });
                }
                if (btnDl) {
                    btnDl.addEventListener('click', () => {
                        void downloadPassFile(downloadHref);
                    });
                }
            }
        });
    }

    return (
        <div className="mt-6">
            <select
                onChange={(e) => setRaison(e.target.value)}
                className="w-full p-4 rounded-md border bg-background text-sm"
            >
                <option value="">Choisissez la raison concernée</option>
                <option value="Je change de moto">Je change de moto</option>
                <option value="Je souhaite résilier mon contrat">Je souhaite résilier mon contrat</option>
                <option value="C'est une demande de mon assureur auto">C'est une demande de mon assureur auto</option>
                <option value="Je souhaite faire un devis à la concurrence">Je souhaite faire un devis à la concurrence</option>
                <option value="Autre motif">Autre motif</option>
            </select>

            {raison && (
                <div className="mt-6">
                    {hasContractApporteur43397 && RDV_REASONS.includes(raison) && bookingUrl ? (
                        <button
                            type="button"
                            onClick={handleDownloadWithRDV}
                            className={linkClassName}
                        >
                            <Download className="w-4 h-4" />
                            {title || "Télécharger mon Relevé de sinistres"}
                        </button>
                    ) : (
                        <APILink
                            href={downloadHref}
                            className={linkClassName}
                            onClick={(e) => handleClick(e)}
                            download
                        >
                            <Download className="w-4 h-4" />
                            {title || "Télécharger mon Relevé de sinistres"}
                        </APILink>
                    )}
                </div>
            )}
        </div>
    );
};

export default FormReleveInformation;
